import { Link } from "react-router-dom";
import {
  ArrowRight,
  Phone,
  Clock,
  Wrench,
  ShieldCheck,
  Activity,
} from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { COMPANY } from "../i18n/translations";
import Seo from "../components/Seo";

const HERO_IMG =
  "https://images.pexels.com/photos/17320030/pexels-photo-17320030.jpeg";
const SERVICES_IMG =
  "https://images.pexels.com/photos/2880871/pexels-photo-2880871.jpeg";

const Home = () => {
  const { t } = useLang();

  return (
    <div data-testid="home-page">
      <Seo pageKey="home" />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Industrial conveyor"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/80 to-transparent" />
          <div className="absolute inset-0 grid-bg opacity-30" />
        </div>

        <div className="relative mx-auto max-w-[1400px] px-5 lg:px-10 pt-20 lg:pt-32 pb-20 lg:pb-40">
          <div className="flex items-center gap-3 mb-8">
            <span className="block w-10 h-px bg-[#FF4500]" />
            <span className="text-overline text-[#FF4500]">{t.home.eyebrow}</span>
          </div>

          <h1
            data-testid="home-hero-title"
            className="font-display text-5xl sm:text-6xl lg:text-8xl uppercase leading-[0.9] text-white max-w-5xl whitespace-pre-line"
          >
            {t.home.heroTitle}
          </h1>

          <p className="mt-8 text-base lg:text-lg text-white/70 max-w-2xl leading-relaxed">
            {t.home.heroSub}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              to="/contact"
              data-testid="home-cta-primary"
              className="group inline-flex items-center gap-3 bg-[#FF4500] hover:bg-[#FF5722] text-black px-6 py-4 font-bold uppercase tracking-widest text-sm transition-colors"
            >
              {t.home.ctaPrimary}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/services"
              data-testid="home-cta-secondary"
              className="inline-flex items-center gap-3 border border-white/30 hover:border-white text-white px-6 py-4 font-bold uppercase tracking-widest text-sm transition-colors"
            >
              {t.home.ctaSecondary}
            </Link>
            <a
              href={`tel:${COMPANY.phoneDial}`}
              data-testid="home-call"
              className="inline-flex items-center gap-3 text-white/70 hover:text-white px-2 py-4 font-bold uppercase tracking-widest text-sm transition-colors"
            >
              <Phone className="w-4 h-4 text-[#FF4500]" />
              {COMPANY.phone}
            </a>
          </div>

          {/* Stats strip */}
          <div className="mt-20 lg:mt-32 grid grid-cols-2 md:grid-cols-4 border-t border-white/10">
            {[
              { v: "16+", l: t.common.yearsExperience },
              { v: "800+", l: t.common.projectsDone },
              { v: "12", l: t.common.industries },
              { v: "24/7", l: t.common.coverage },
            ].map((s) => (
              <div
                key={s.v}
                className="px-4 py-6 border-r last:border-r-0 border-white/10"
              >
                <div className="font-display text-4xl lg:text-5xl text-white">
                  {s.v}
                </div>
                <div className="text-overline text-white/50 mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="relative bg-[#050505]">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-6">
                <span className="block w-10 h-px bg-[#FF4500]" />
                <span className="text-overline text-[#FF4500]">{t.home.whatWeDo}</span>
              </div>
              <h2 className="font-display text-4xl lg:text-6xl uppercase leading-[0.95] text-white">
                {t.home.whatWeDoTitle}
              </h2>
              <p className="mt-6 text-white/70 leading-relaxed">{t.home.whatWeDoSub}</p>
              <Link
                to="/services"
                data-testid="home-services-link"
                className="mt-8 inline-flex items-center gap-2 text-[#FF4500] hover:text-[#FF5722] font-bold uppercase tracking-widest text-sm group"
              >
                {t.nav.services}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {t.services.list.slice(0, 4).map((s) => {
                const Icon =
                  s.id === "hot"
                    ? Wrench
                    : s.id === "cold"
                      ? Activity
                      : s.id === "splice"
                        ? ShieldCheck
                        : Clock;
                return (
                  <Link
                    key={s.id}
                    to="/services"
                    data-testid={`home-service-${s.id}`}
                    className="group block p-6 border border-white/10 hover:border-[#FF4500] bg-[#0a0a0a] hover:bg-[#0f0f0f] transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <span className="text-overline text-white/40">{s.n}</span>
                      <Icon className="w-5 h-5 text-[#FF4500]" />
                    </div>
                    <h3 className="mt-6 font-display text-2xl uppercase text-white group-hover:text-[#FF4500] transition-colors">
                      {s.t}
                    </h3>
                    <p className="mt-3 text-sm text-white/60 leading-relaxed line-clamp-3">
                      {s.d}
                    </p>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="relative border-y border-white/10 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={SERVICES_IMG}
            alt="Welder"
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-[#050505]/70" />
        </div>
        <div className="relative mx-auto max-w-[1400px] px-5 lg:px-10 py-20 lg:py-28">
          <div className="flex items-center gap-3 mb-6">
            <span className="block w-10 h-px bg-[#FF4500]" />
            <span className="text-overline text-[#FF4500]">{t.home.whyUs}</span>
          </div>
          <h2 className="font-display text-4xl lg:text-6xl uppercase leading-[0.95] text-white max-w-3xl">
            {t.home.whyUsTitle}
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {t.home.reasons.map((r) => (
              <div
                key={r.k}
                className="bg-[#0a0a0a] p-8 hover:bg-[#0f0f0f] transition-colors"
              >
                <div className="font-display text-5xl text-[#FF4500]">{r.k}</div>
                <h4 className="mt-6 font-display text-xl uppercase text-white">
                  {r.t}
                </h4>
                <p className="mt-3 text-sm text-white/60 leading-relaxed">{r.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="bg-[#FF4500] text-black">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-12 lg:py-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <h3 className="font-display text-3xl lg:text-5xl uppercase leading-[0.95]">
              {t.home.ctaBannerTitle}
            </h3>
            <p className="mt-3 text-black/80">{t.home.ctaBannerSub}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/contact"
              data-testid="home-banner-cta"
              className="inline-flex items-center gap-3 bg-black hover:bg-[#1a1a1a] text-white px-6 py-4 font-bold uppercase tracking-widest text-sm"
            >
              {t.home.ctaPrimary}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={`tel:${COMPANY.phoneDial}`}
              data-testid="home-banner-call"
              className="inline-flex items-center gap-3 border border-black hover:bg-black hover:text-white text-black px-6 py-4 font-bold uppercase tracking-widest text-sm transition-colors"
            >
              <Phone className="w-4 h-4" />
              {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
